import { Tab } from '@headlessui/react';
import ColorPalette from 'components/Traits/ColorPalette';
import MusicStructure from 'components/Traits/MusicStructure';
import NoteShape from 'components/Traits/NoteShape';
import SongTraits from 'components/Traits/SongTraits';
import { Box, ClickableBox, DropdownItem, Flex } from 'components/ui';
import { Fragment } from 'react';
import styled from 'styled-components/macro';
import TwistsAndDistortions from 'components/Traits/TwistsAndDistortions';
import Effects from 'components/Traits/Effects';
import { useWindowSize } from 'hooks/useWindowSize';
import { SCREEN_SIZES } from 'utils/styles';
import FuturaMaster from 'components/Traits/FuturaMaster';

interface TraitTabsProps {}

const Container = styled.div`
  margin: 16px auto;
  max-width: 1200px;
  width: 100%;
  padding: 0 16px;

  a {
    color: white !important;
  }

  ul > li {
      margin: 16px 0;
    }
  }

  ol > li {
    margin: 8px 0;
  }
`;

const TraitTab = ({ children, ...rest }) => {
  return (
    <Tab as={Fragment}>
      {({ selected }) => (
        <ClickableBox
          fontSize="14px"
          fontWeight={selected ? 700 : 400}
          lineHeight={1.43}
          borderBottomWidth="3px"
          borderBottomColor={selected ? '#9A2E98' : 'transparent'}
          borderBottomStyle="solid"
          paddingBottom="6px"
          {...rest}
        >
          {children}
        </ClickableBox>
      )}
    </Tab>
  );
};

const TraitPanel = ({ children }) => {
  return <Tab.Panel as={Fragment}>{({ selected }) => <Container>{children}</Container>}</Tab.Panel>;
};

const TRAIT_TABS = [
  {
    name: 'Music Structure',
    component: MusicStructure,
  },
  {
    name: 'Song Traits',
    component: SongTraits,
  },
  {
    name: 'Color Palettes',
    component: ColorPalette,
  },
  {
    name: 'Note Shapes',
    component: NoteShape,
  },
  {
    name: 'Twists and Distortions',
    component: TwistsAndDistortions,
  },
  {
    name: 'Effects',
    component: Effects,
  },
  {
    name: 'Futura Master',
    component: FuturaMaster,
  },
];

const TraitTabs: React.FC<TraitTabsProps> = () => {
  const ws = useWindowSize();

  return (
    <Tab.Group>
      <Tab.List as={Fragment}>
        {({ selectedIndex }) => {
          return ws.width > SCREEN_SIZES.sm ? (
            <Box
              width="100%"
              display="flex"
              flexWrap="nowrap"
              justifyContent="space-between"
              marginBottom="52px"
              paddingX="16px"
            >
              {TRAIT_TABS.map((tab, index) => (
                <TraitTab key={tab.name}>{tab.name}</TraitTab>
              ))}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center">
              <DropdownItem title={TRAIT_TABS[selectedIndex].name}>
                <Flex flexDirection="column">
                  {TRAIT_TABS.map((tab, index) => (
                    <TraitTab key={tab.name} marginY="4px" minWidth="160px">
                      {tab.name}
                    </TraitTab>
                  ))}
                </Flex>
              </DropdownItem>
            </Box>
          );
        }}
      </Tab.List>
      <Tab.Panels>
        {TRAIT_TABS.map((tab, index) => (
          <TraitPanel key={tab.name}>
            <tab.component />
          </TraitPanel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
};

export default TraitTabs;
